import { createSelector, createSlice } from "@reduxjs/toolkit"
import * as dateFunctions from "date-fns"

import type { PayloadAction, Reducer } from "@reduxjs/toolkit"

import { createHandler } from "@/base/send-request"
import * as Models from "@/redux/models"

import type { RootState } from "./root.reducer"

import { signOutAsync } from "./auth.reducer"
import { getFetchStatusByName } from "./requests.reducer"

/**
 * Load farm-wide timeline of device state transitions for the active farm.
 * Defined here (rather than in `farmhq-api.ts`) so this reducer can reference
 * `.fulfilled` at module-init time without hitting the circular import that
 * leaves the symbol undefined when imported back from farmhq-api.
 */
export const loadFarmTimelineEventsAsync = createHandler(
  "LoadFarmTimelineEvents",
  {
    demoMode: {
      allowRequest: true,
    },
  },
)

/**
 * Selectable time windows for the farm timeline view. The backend enforces a
 * 7-day max so the longest option here matches that cap.
 */
export const TIMELINE_WINDOW_OPTIONS = ["24h", "3d", "7d"] as const
export type TimelineWindow = (typeof TIMELINE_WINDOW_OPTIONS)[number]

const WINDOW_TO_HOURS: Record<TimelineWindow, number> = {
  "24h": 24,
  "3d": 72,
  "7d": 168,
}

export function getMinDateForWindow(window: TimelineWindow): Date {
  return dateFunctions.subHours(new Date(), WINDOW_TO_HOURS[window])
}

interface FarmTimelineState {
  events: Models.FarmTimelineEvent[]
  window: TimelineWindow
}

const initialState: FarmTimelineState = {
  events: [],
  window: "24h",
}

const slice = createSlice({
  extraReducers: (builder) =>
    builder
      .addCase(loadFarmTimelineEventsAsync.fulfilled, (state, { payload }) => {
        state.events = payload.items
      })
      .addCase(signOutAsync.fulfilled, () => ({ ...initialState })),
  initialState,
  name: "farmTimeline",
  reducers: {
    setTimelineWindow: (state, { payload }: PayloadAction<TimelineWindow>) => {
      state.window = payload
    },
  },
})

const farmTimeline: Reducer<FarmTimelineState> = slice.reducer
export default farmTimeline
export const { setTimelineWindow } = slice.actions

export function getTimelineWindowFromState(state: RootState): TimelineWindow {
  return state.farmTimeline.window
}

export function getTimelineEventsFromState(
  state: RootState,
): Models.FarmTimelineEvent[] {
  return state.farmTimeline.events
}

export function getIsTimelineLoading(state: RootState): boolean {
  return getFetchStatusByName(state, "LoadFarmTimelineEvents") === "pending"
}

// ---------- Aggregation -----------------------------------------------------
//
// The backend returns one record per state-column transition for pressure,
// reel, and relay sensors. The selectors below collapse those raw rows into
// three kinds of user-facing entries:
//
// 1. `session`        — a paired start→stop run (pressurized, reel moving,
//                        relay on) with duration
// 2. `transition`     — a one-off over-pressure alarm that isn't part of a
//                        burst
// 3. `alarm_burst`    — 5+ over-pressure transitions on the same device
//                        within a 15-minute window, collapsed into one row
//
// Entries are returned newest-first.

const PRESSURE_PRESSURIZED_STATES = new Set(["PHI", "POV"])
const PRESSURE_OVER_STATE = "POV"

const REEL_MOVING_STATES = new Set(["RR", "RE"])

const RELAY_ON_STATE = "ON"

export type SessionSignal = "pressure" | "reel" | "relay"
export type TransitionKind = "over_pressure"

export interface TimelineSessionEntry {
  kind: "session"
  key: string
  deviceId: string
  signal: SessionSignal
  startTimestamp: string
  endTimestamp: string | null
  /** null when the session has no matching stop yet (still ongoing). */
  durationMs: number | null
  /** The most recent timestamp belonging to this entry — used for sorting. */
  sortTimestamp: string
}

export interface TimelineTransitionEntry {
  kind: "transition"
  key: string
  deviceId: string
  timestamp: string
  transitionType: TransitionKind
  fromState: string | null
  toState: string
  sortTimestamp: string
}

export interface TimelineAlarmBurstEntry {
  kind: "alarm_burst"
  key: string
  deviceId: string
  transitionType: TransitionKind
  startTimestamp: string
  endTimestamp: string
  count: number
  sortTimestamp: string
}

export type TimelineEntry =
  | TimelineSessionEntry
  | TimelineTransitionEntry
  | TimelineAlarmBurstEntry

interface OpenSession {
  startTimestamp: string
}

/**
 * 15 minutes — width of the sliding window used to detect bursts of repeated
 * alarms of the same kind on the same device.
 */
const BURST_WINDOW_MS = 15 * 60 * 1000
const BURST_MIN_COUNT = 5

function isPressureStart(to: string | null, from: string | null): boolean {
  return (
    to != null &&
    PRESSURE_PRESSURIZED_STATES.has(to) &&
    !PRESSURE_PRESSURIZED_STATES.has(from ?? "")
  )
}
function isPressureStop(to: string | null, from: string | null): boolean {
  return (
    to == null ||
    (!PRESSURE_PRESSURIZED_STATES.has(to) &&
      PRESSURE_PRESSURIZED_STATES.has(from ?? ""))
  )
}

function isReelStart(to: string | null, from: string | null): boolean {
  return (
    to != null &&
    REEL_MOVING_STATES.has(to) &&
    !REEL_MOVING_STATES.has(from ?? "")
  )
}
function isReelStop(to: string | null, from: string | null): boolean {
  return (
    to != null &&
    !REEL_MOVING_STATES.has(to) &&
    REEL_MOVING_STATES.has(from ?? "")
  )
}

function isRelayStart(to: string | null, from: string | null): boolean {
  return to === RELAY_ON_STATE && from !== RELAY_ON_STATE
}
function isRelayStop(to: string | null, from: string | null): boolean {
  return (
    to != null && to !== RELAY_ON_STATE && from === RELAY_ON_STATE
  )
}

/**
 * Group consecutive same-kind alarms on a device into one burst entry when
 * they occur within BURST_WINDOW_MS of each other and at least BURST_MIN_COUNT
 * fire in total.
 */
function collapseBursts(
  candidates: TimelineTransitionEntry[],
): Array<TimelineTransitionEntry | TimelineAlarmBurstEntry> {
  if (candidates.length === 0) return []
  const result: Array<TimelineTransitionEntry | TimelineAlarmBurstEntry> = []

  // Walk in ascending time so the sliding-window check is straightforward.
  const ascending = [...candidates].sort((a, b) =>
    a.timestamp.localeCompare(b.timestamp),
  )

  let i = 0
  while (i < ascending.length) {
    const start = ascending[i]
    if (start == null) break
    let j = i + 1
    while (j < ascending.length) {
      const candidate = ascending[j]
      const prev = ascending[j - 1]
      if (candidate == null || prev == null) break
      const gap =
        new Date(candidate.timestamp).getTime() -
        new Date(prev.timestamp).getTime()
      if (
        candidate.deviceId !== start.deviceId ||
        candidate.transitionType !== start.transitionType ||
        gap > BURST_WINDOW_MS
      ) {
        break
      }
      j += 1
    }
    const runLength = j - i
    const last = ascending[j - 1]
    if (runLength >= BURST_MIN_COUNT && last != null) {
      result.push({
        count: runLength,
        deviceId: start.deviceId,
        endTimestamp: last.timestamp,
        key: `burst:${start.deviceId}:${start.transitionType}:${start.timestamp}`,
        kind: "alarm_burst",
        sortTimestamp: last.timestamp,
        startTimestamp: start.timestamp,
        transitionType: start.transitionType,
      })
    } else {
      for (let k = i; k < j; k += 1) {
        const entry = ascending[k]
        if (entry != null) result.push(entry)
      }
    }
    i = j
  }
  return result
}

/**
 * Convert raw backend rows into a chronological list of TimelineEntry. Pairs
 * start→stop transitions of the same signal on the same device into session
 * entries and collapses dense over-pressure bursts.
 */
export function aggregateTimelineEvents(
  events: Models.FarmTimelineEvent[],
): TimelineEntry[] {
  // Group by device so we can pair start/stop in order.
  const byDevice = new Map<string, Models.FarmTimelineEvent[]>()
  for (const event of events) {
    const list = byDevice.get(event.deviceId)
    if (list == null) {
      byDevice.set(event.deviceId, [event])
    } else {
      list.push(event)
    }
  }

  const sessionEntries: TimelineSessionEntry[] = []
  const alarmCandidates: TimelineTransitionEntry[] = []

  for (const [deviceId, deviceEvents] of byDevice) {
    const ascending = [...deviceEvents].sort((a, b) =>
      a.timestamp.localeCompare(b.timestamp),
    )
    const open: Partial<Record<SessionSignal, OpenSession>> = {}

    for (const event of ascending) {
      pairSession({
        deviceId,
        event,
        from: event.pressureStatePrevious,
        isStart: isPressureStart,
        isStop: isPressureStop,
        open,
        sessionEntries,
        signal: "pressure",
        to: event.pressureStateCurrent,
      })
      pairSession({
        deviceId,
        event,
        from: event.reelStatePrevious,
        isStart: isReelStart,
        isStop: isReelStop,
        open,
        sessionEntries,
        signal: "reel",
        to: event.reelStateCurrent,
      })
      pairSession({
        deviceId,
        event,
        from: event.relayStatePrevious,
        isStart: isRelayStart,
        isStop: isRelayStop,
        open,
        sessionEntries,
        signal: "relay",
        to: event.relayStateCurrent,
      })

      const pressureTo = event.pressureStateCurrent
      const pressureFrom = event.pressureStatePrevious
      if (
        pressureTo === PRESSURE_OVER_STATE &&
        pressureFrom !== PRESSURE_OVER_STATE
      ) {
        alarmCandidates.push({
          deviceId,
          fromState: pressureFrom,
          key: `tx:pressure:${event.id}`,
          kind: "transition",
          sortTimestamp: event.timestamp,
          timestamp: event.timestamp,
          toState: pressureTo,
          transitionType: "over_pressure",
        })
      }
    }

    // Sessions still open at the end of the window are emitted with no end.
    for (const signalKey of Object.keys(open) as SessionSignal[]) {
      const session = open[signalKey]
      if (session) {
        sessionEntries.push({
          deviceId,
          durationMs: null,
          endTimestamp: null,
          key: `session:${signalKey}:${deviceId}:${session.startTimestamp}:open`,
          kind: "session",
          signal: signalKey,
          sortTimestamp: session.startTimestamp,
          startTimestamp: session.startTimestamp,
        })
      }
    }
  }

  const collapsed = collapseBursts(alarmCandidates)
  const combined: TimelineEntry[] = [...sessionEntries, ...collapsed]
  combined.sort((a, b) => b.sortTimestamp.localeCompare(a.sortTimestamp))
  return combined
}

function pairSession(args: {
  deviceId: string
  event: Models.FarmTimelineEvent
  from: string | null
  isStart: (to: string | null, from: string | null) => boolean
  isStop: (to: string | null, from: string | null) => boolean
  open: Partial<Record<SessionSignal, OpenSession>>
  sessionEntries: TimelineSessionEntry[]
  signal: SessionSignal
  to: string | null
}): void {
  const { deviceId, event, from, isStart, isStop, open, sessionEntries, signal, to } = args
  if (isStart(to, from)) {
    if (!open[signal]) {
      open[signal] = { startTimestamp: event.timestamp }
    }
  } else if (isStop(to, from)) {
    const session = open[signal]
    if (session) {
      sessionEntries.push({
        deviceId,
        durationMs:
          new Date(event.timestamp).getTime() -
          new Date(session.startTimestamp).getTime(),
        endTimestamp: event.timestamp,
        key: `session:${signal}:${deviceId}:${session.startTimestamp}`,
        kind: "session",
        signal,
        sortTimestamp: event.timestamp,
        startTimestamp: session.startTimestamp,
      })
      open[signal] = undefined
    }
  }
}

export interface TimelineDayBucket {
  /** YYYY-MM-DD — stable key for the bucket. */
  key: string
  /** Midnight of the bucket day, for header formatting. */
  date: Date
  entries: TimelineEntry[]
}

function bucketKeyForTimestamp(timestamp: string): string {
  return dateFunctions.format(new Date(timestamp), "yyyy-MM-dd")
}

export function bucketEntriesByDay(
  entries: TimelineEntry[],
): TimelineDayBucket[] {
  const buckets = new Map<string, TimelineDayBucket>()
  for (const entry of entries) {
    const key = bucketKeyForTimestamp(entry.sortTimestamp)
    let bucket = buckets.get(key)
    if (!bucket) {
      bucket = {
        date: dateFunctions.startOfDay(new Date(entry.sortTimestamp)),
        entries: [],
        key,
      }
      buckets.set(key, bucket)
    }
    bucket.entries.push(entry)
  }
  return Array.from(buckets.values()).sort((a, b) =>
    b.date.getTime() - a.date.getTime(),
  )
}

export const getAggregatedTimelineEntries = createSelector(
  [getTimelineEventsFromState],
  aggregateTimelineEvents,
)

export const getTimelineDayBuckets = createSelector(
  [getAggregatedTimelineEntries],
  bucketEntriesByDay,
)
